import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import { SunIcon, MoonIcon } from '@heroicons/react/24/outline';

const Layout: React.FC = () => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const storedTheme = localStorage.getItem('theme');
    if (storedTheme !== null) {
      return storedTheme === 'dark';
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  const [isSidebarOpen, setSidebarOpen] = useState(() => {
    const storedSidebarState = localStorage.getItem('sidebarOpen');
    if (storedSidebarState !== null) {
      return storedSidebarState === 'true';
    }
    return window.innerWidth >= 768;
  });

  const toggleTheme = () => {
    setIsDarkMode(prev => {
      const newTheme = !prev;
      localStorage.setItem('theme', newTheme ? 'dark' : 'light');
      return newTheme;
    });
  };

  const toggleSidebar = () => {
    setSidebarOpen(prev => {
      const newState = !prev;
      localStorage.setItem('sidebarOpen', String(newState));
      return newState;
    });
  };

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      document.documentElement.classList.remove('light');
    } else {
      document.documentElement.classList.add('light');
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      if (localStorage.getItem('theme') === null) {
        setIsDarkMode(e.matches);
      }
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return (
    <div className="relative h-[calc(100vh-32px)] flex bg-light-background dark:bg-dark-background">
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gray-200 dark:bg-gray-500/50" />
      <Sidebar 
        isOpen={isSidebarOpen} 
        onToggle={toggleSidebar}
        isDarkMode={isDarkMode}
      />
      
      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-200 ${isSidebarOpen ? 'md:ml-64' : 'ml-0'}`}>
        <Header 
          isSidebarOpen={isSidebarOpen} 
          onToggleSidebar={toggleSidebar}
          isDarkMode={isDarkMode}
          onToggleTheme={toggleTheme}
        />
        
        <main className="flex-1 p-6 mt-16 overflow-y-auto min-w-0 bg-light-background dark:bg-dark-background">
          <Outlet />
        </main>

        {/* Theme Toggle Button (Mobile) */}
        <button
          onClick={toggleTheme}
          className="md:hidden fixed bottom-6 right-6 p-3 rounded-full bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border shadow-lg"
          aria-label="Toggle theme"
        >
          {isDarkMode ? (
            <SunIcon className="w-6 h-6 text-primary-500" />
          ) : (
            <MoonIcon className="w-6 h-6 text-primary-500" />
          )}
        </button>
      </div>
    </div>
  );
};

export default Layout;